import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { Listbox } from '@headlessui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getImportanceLevel } from './utils';
import {
  IMPORTANCE_BRACKETS,
  ImportanceLevelListboxProps,
  UNBREAKABLE_LEVEL_VALUE,
} from './types';

/**
 * A reusable Listbox component for selecting importance levels.
 *
 * Note: We need to stop event propagation on all click events to prevent
 * the parent section toggle from being triggered when interacting with
 * the dropdown. This is because the Listbox is rendered inside a button
 * that toggles the section's expanded state.
 */
export default function ImportanceLevelListbox({
  value,
  onChange,
  className = '',
}: ImportanceLevelListboxProps) {
  const { t } = useTranslation();
  const { level, bgColor, color, label } = getImportanceLevel(value);

  return (
    <Listbox value={level} onChange={onChange}>
      <div
        className="relative"
        onClick={e => e.stopPropagation() /* Prevent section toggle */}
      >
        <Listbox.Button
          role="combobox"
          aria-expanded={true}
          aria-haspopup="listbox"
          className={`relative w-40 cursor-pointer rounded-md py-1 px-2 text-left ${bgColor} ${color} text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
          onClick={e => e.stopPropagation() /* Prevent section toggle */}
        >
          <span className="block truncate pr-8">{t(label)}</span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-4 w-4" aria-hidden="true" />
          </span>
        </Listbox.Button>
        <Listbox.Options
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
          onClick={e => e.stopPropagation() /* Prevent section toggle */}
        >
          {Object.values(IMPORTANCE_BRACKETS).map(bracket => (
            <Listbox.Option
              key={bracket.level}
              value={bracket.level}
              className={({ active }) =>
                `${
                  active
                    ? 'bg-blue-600 text-white'
                    : `${bracket.bgColor} ${bracket.color}`
                } relative cursor-pointer select-none py-2 px-3`
              }
              onClick={e => e.stopPropagation()}
            >
              {({ selected, active }) => (
                <>
                  <span
                    className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                  >
                    {t(bracket.label)}
                  </span>
                  {selected && (
                    <span
                      className={`${
                        active ? 'text-white' : 'text-blue-600'
                      } absolute inset-y-0 right-0 flex items-center pr-4`}
                    >
                      ✓
                    </span>
                  )}
                </>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </div>
    </Listbox>
  );
}
